import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, RefreshCw, Download, Eye, Trash2, Edit,
  Clock, CheckCircle, XCircle, Loader2, FileSpreadsheet, Plus, UserPlus, X
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";

import AddDealModal from "./deals/AddDealModal";
import { DSP_OPTIONS, STATUS_OPTIONS, SSP_OPTIONS, userLabel } from "./deals/dealOptions";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const getStatusBadge = (status) => {
  const cls = {
    pending: "bg-yellow-500/20 text-yellow-400",
    approved: "bg-green-500/20 text-green-400",
    delivered: "bg-blue-500/20 text-blue-400",
    rejected: "bg-red-500/20 text-red-400",
  }[status] || "bg-gray-500/20 text-gray-400";
  return <Badge className={`${cls} border-0`}>{status?.charAt(0).toUpperCase() + status?.slice(1)}</Badge>;
};

const formatDate = (d) => {
  if (!d) return "-";
  return new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const DealsTab = ({ token, isSuperAdmin, canEdit, requestDelete }) => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterDsp, setFilterDsp] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [sspDeals, setSspDeals] = useState([]);
  const [newSsp, setNewSsp] = useState("");
  const [newSspDealId, setNewSspDealId] = useState("");
  const [newSspDealName, setNewSspDealName] = useState("");
  const [savingSsp, setSavingSsp] = useState(false);
  const [users, setUsers] = useState([]);
  const [addOpen, setAddOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkUserId, setBulkUserId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [ownerDraft, setOwnerDraft] = useState("");

  const fetchDeals = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterDsp) params.append("dsp", filterDsp);
      if (filterStatus) params.append("status", filterStatus);
      const response = await axios.get(`${API}/deals?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDeals(response.data);
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 403) {
        localStorage.clear();
        navigate("/admin");
        toast.error("Session expired or access denied.");
      } else {
        toast.error("Failed to fetch deals");
      }
    } finally {
      setLoading(false);
    }
  }, [token, search, filterDsp, filterStatus, navigate]);

  useEffect(() => { fetchDeals(); }, [fetchDeals]);

  useEffect(() => {
    if (!canEdit) return;
    axios.get(`${API}/deals/assignable-users`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setUsers(res.data))
      .catch(() => {});
  }, [canEdit, token]);

  const toggleSelect = (dealId) => setSelectedIds(prev =>
    prev.includes(dealId) ? prev.filter(id => id !== dealId) : [...prev, dealId]
  );

  const toggleSelectAll = () => setSelectedIds(prev => prev.length === deals.length ? [] : deals.map(d => d.id));

  const applyOwnerToDeals = (dealIds, res) => {
    setDeals(prev => prev.map(d => dealIds.includes(d.id)
      ? { ...d, user_id: res.user_id, owner_name: res.owner_name, owner_email: res.owner_email, owner_role: res.owner_role }
      : d));
  };

  const handleAssignOwner = async () => {
    if (!selectedDeal) return;
    setAssigning(true);
    try {
      const res = await axios.patch(`${API}/deals/${selectedDeal.id}/assign`, { user_id: ownerDraft || null }, { headers: { Authorization: `Bearer ${token}` } });
      applyOwnerToDeals([selectedDeal.id], res.data);
      setSelectedDeal(prev => ({ ...prev, user_id: res.data.user_id, owner_name: res.data.owner_name, owner_email: res.data.owner_email }));
      toast.success(res.data.message);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to assign deal"); }
    finally { setAssigning(false); }
  };

  const handleBulkAssign = async () => {
    if (selectedIds.length === 0) return;
    setAssigning(true);
    try {
      const res = await axios.post(`${API}/deals/bulk-assign`, { deal_ids: selectedIds, user_id: bulkUserId || null }, { headers: { Authorization: `Bearer ${token}` } });
      applyOwnerToDeals(selectedIds, res.data);
      toast.success(res.data.message);
      setSelectedIds([]);
      setBulkUserId("");
    } catch (e) { toast.error(e.response?.data?.detail || "Bulk assign failed"); }
    finally { setAssigning(false); }
  };

  const stats = useMemo(() => ({    total: deals.length,
    pending: deals.filter(d => d.status === "pending").length,
    approved: deals.filter(d => d.status === "approved").length,
    rejected: deals.filter(d => d.status === "rejected").length,
  }), [deals]);

  const colCount = 8 + (canEdit ? 1 : 0) + (isSuperAdmin ? 1 : 0);

  const handleStatusChange = async (dealId, newStatus) => {
    try {
      await axios.patch(`${API}/deals/${dealId}/status?status=${newStatus}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setDeals(prev => prev.map(d => d.id === dealId ? { ...d, status: newStatus } : d));
      toast.success(`Deal status updated to ${newStatus}`);
    } catch { toast.error("Failed to update status"); }
  };

  const handleDelete = (dealId) => {
    const deal = deals.find(d => d.id === dealId);
    requestDelete("deal", dealId, deal?.company || "this deal", async () => {
      try {
        await axios.delete(`${API}/deals/${dealId}`, { headers: { Authorization: `Bearer ${token}` } });
        setDeals(prev => prev.filter(d => d.id !== dealId));
        toast.success("Deal deleted");
      } catch { toast.error("Failed to delete deal"); }
    });
  };

  const handleExport = async () => {
    try {
      const response = await axios.get(`${API}/deals/export`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'deals_export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { toast.error("Export failed"); }
  };

  const openDealDetails = (deal) => {
    setSelectedDeal(deal);
    setSspDeals(deal.ssp_deals || []);
    setOwnerDraft(deal.user_id || "");
    setDetailsOpen(true);
  };

  const handleAddSsp = () => {
    if (!newSsp || !newSspDealId) { toast.error("Select SSP and enter Deal ID"); return; }
    setSspDeals(prev => [...prev, { ssp: newSsp, deal_id: newSspDealId, deal_name: newSspDealName || "" }]);
    setNewSsp(""); setNewSspDealId(""); setNewSspDealName("");
  };

  const handleRemoveSsp = (index) => setSspDeals(prev => prev.filter((_, i) => i !== index));

  const handleSaveSspDeals = async () => {
    if (!selectedDeal) return;
    setSavingSsp(true);
    try {
      const res = await axios.put(`${API}/deals/${selectedDeal.id}/ssp-deals`, { ssp_deals: sspDeals }, { headers: { Authorization: `Bearer ${token}` } });
      setDeals(prev => prev.map(d => d.id === selectedDeal.id ? { ...d, ssp_deals: res.data.ssp_deals, status: res.data.status } : d));
      setSelectedDeal(res.data);
      toast.success("SSP deals saved");
    } catch { toast.error("Failed to save SSP deals"); }
    finally { setSavingSsp(false); }
  };

  return (
    <>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-white font-mono">Deal Requests</h1>
          <p className="text-[#ebebeb]/60 mt-1 font-mono text-sm">{canEdit ? 'Manage and review incoming deal requests' : 'View incoming deal requests'}</p>
        </div>
        <div className="flex items-center gap-3">
          {canEdit && (
            <button onClick={() => setAddOpen(true)} className="flex items-center gap-2 py-2 px-4 rounded-full border border-[#ff3737]/40 text-[#ff3737] hover:bg-[#ff3737]/10 transition-colors font-mono text-sm" data-testid="add-deal-btn">
              <Plus className="w-4 h-4" /> Add Deal
            </button>
          )}
          <button onClick={fetchDeals} className="flex items-center gap-2 py-2 px-4 rounded-full border border-white/10 text-[#ebebeb]/60 hover:text-white hover:bg-white/5 transition-colors font-mono text-sm" data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button onClick={handleExport} className="ingenium-button" data-testid="export-btn">
            <span className="flex items-center gap-2"><Download className="w-4 h-4" /> Export CSV</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {[
          { label: "Total Deals", value: stats.total, color: "text-white" },
          { label: "Pending", value: stats.pending, color: "text-yellow-400", icon: <Clock className="w-4 h-4 text-yellow-400" /> },
          { label: "Approved", value: stats.approved, color: "text-green-400", icon: <CheckCircle className="w-4 h-4 text-green-400" /> },
          { label: "Rejected", value: stats.rejected, color: "text-red-400", icon: <XCircle className="w-4 h-4 text-red-400" /> },
        ].map((s) => (
          <Card key={s.label} className="feature-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-[#ebebeb]/60 font-mono flex items-center gap-2">{s.icon}{s.label}</CardTitle>
            </CardHeader>
            <CardContent><div className={`text-2xl font-bold font-mono ${s.color}`}>{s.value}</div></CardContent>
          </Card>
        ))}
      </div>

      <Card className="feature-card mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#ebebeb]/40" />
              <Input placeholder="Search by name, email, or company..." value={search} onChange={(e) => setSearch(e.target.value)} className="form-input pl-10" data-testid="search-input" />
            </div>
            <div className="flex gap-3">
              <select value={filterDsp} onChange={(e) => setFilterDsp(e.target.value)} className="form-input px-3 py-2 rounded-md text-sm" data-testid="filter-dsp">
                <option value="">All DSPs</option>
                {DSP_OPTIONS.map((dsp) => (<option key={dsp} value={dsp}>{dsp}</option>))}
              </select>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="form-input px-3 py-2 rounded-md text-sm" data-testid="filter-status">
                <option value="">All Status</option>
                {STATUS_OPTIONS.map((s) => (<option key={s.value} value={s.value}>{s.label}</option>))}
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {canEdit && selectedIds.length > 0 && (
        <Card className="feature-card mb-4 border-[#ff3737]/30" data-testid="bulk-assign-bar">
          <CardContent className="p-4 flex flex-col lg:flex-row lg:items-center gap-3">
            <p className="text-white font-mono text-sm flex-1">
              <span className="text-[#ff3737]">{selectedIds.length}</span> deal{selectedIds.length > 1 ? "s" : ""} selected
            </p>
            <select value={bulkUserId} onChange={(e) => setBulkUserId(e.target.value)} className="form-input px-3 py-2 rounded-md text-sm lg:w-96" data-testid="bulk-assign-select">
              <option value="">Unassigned</option>
              {users.map((u) => (<option key={u.id} value={u.id}>{userLabel(u)}</option>))}
            </select>
            <button onClick={handleBulkAssign} disabled={assigning} className="ingenium-button" data-testid="bulk-assign-apply">
              <span className="flex items-center gap-2">
                {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserPlus className="w-4 h-4" />} Apply
              </span>
            </button>
            <button onClick={() => setSelectedIds([])} className="p-2 text-[#ebebeb]/50 hover:text-white" data-testid="bulk-assign-clear"><X className="w-4 h-4" /></button>
          </CardContent>
        </Card>
      )}

      <Card className="feature-card overflow-hidden">
        <div className="overflow-x-auto">
          <Table className="data-table">
            <TableHeader>
              <TableRow className="border-white/10">
                {canEdit && (
                  <TableHead className="w-10">
                    <input type="checkbox" checked={deals.length > 0 && selectedIds.length === deals.length} onChange={toggleSelectAll} className="accent-[#ff3737] cursor-pointer" data-testid="select-all-deals" />
                  </TableHead>
                )}
                <TableHead className="text-[#ebebeb]/60 font-mono">Name</TableHead>
                <TableHead className="text-[#ebebeb]/60 font-mono">Company</TableHead>
                <TableHead className="text-[#ebebeb]/60 font-mono">Owner</TableHead>
                <TableHead className="text-[#ebebeb]/60 font-mono">DSP</TableHead>
                <TableHead className="text-[#ebebeb]/60 font-mono">Seat #</TableHead>
                <TableHead className="text-[#ebebeb]/60 font-mono">Status</TableHead>
                {isSuperAdmin && <TableHead className="text-[#ebebeb]/60 font-mono">Location</TableHead>}
                <TableHead className="text-[#ebebeb]/60 font-mono">Created</TableHead>
                <TableHead className="text-[#ebebeb]/60 font-mono text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={colCount} className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-[#ff3737] mx-auto" /><p className="text-[#ebebeb]/60 mt-2 font-mono">Loading deals...</p></TableCell></TableRow>
              ) : deals.length === 0 ? (
                <TableRow><TableCell colSpan={colCount} className="text-center py-12"><FileSpreadsheet className="w-12 h-12 text-[#ebebeb]/30 mx-auto mb-3" /><p className="text-[#ebebeb]/60 font-mono">No deals found</p></TableCell></TableRow>
              ) : deals.map((deal) => (
                <TableRow key={deal.id} className="border-white/5" data-testid={`deal-row-${deal.id}`}>
                  {canEdit && (
                    <TableCell>
                      <input type="checkbox" checked={selectedIds.includes(deal.id)} onChange={() => toggleSelect(deal.id)} className="accent-[#ff3737] cursor-pointer" data-testid={`select-deal-${deal.id}`} />
                    </TableCell>
                  )}
                  <TableCell><div><p className="text-white font-mono">{deal.name}</p><p className="text-[#ebebeb]/40 text-xs font-mono">{deal.email}</p></div></TableCell>
                  <TableCell className="text-[#ebebeb]/70 font-mono">{deal.company}</TableCell>
                  <TableCell data-testid={`deal-owner-${deal.id}`}>
                    {deal.owner_email ? (
                      <div><p className="text-white font-mono text-sm">{deal.owner_name || "-"}</p><p className="text-[#ebebeb]/40 text-xs font-mono">{deal.owner_email}</p></div>
                    ) : (
                      <span className="text-[#ebebeb]/30 font-mono text-xs">Unassigned</span>
                    )}
                  </TableCell>
                  <TableCell className="text-[#ebebeb]/70 font-mono">{deal.dsp}</TableCell>
                  <TableCell className="text-[#ebebeb]/70 font-mono text-sm">{deal.dsp_seat_number}</TableCell>
                  <TableCell>
                    {canEdit ? (
                      <select value={deal.status} onChange={(e) => handleStatusChange(deal.id, e.target.value)} className="bg-transparent border-0 text-sm font-mono cursor-pointer" data-testid={`status-select-${deal.id}`}>
                        {STATUS_OPTIONS.map((s) => (<option key={s.value} value={s.value} className="bg-[#191b1d]">{s.label}</option>))}
                      </select>
                    ) : getStatusBadge(deal.status)}
                  </TableCell>
                  {isSuperAdmin && <TableCell className="text-[#ebebeb]/50 text-xs font-mono">{deal.location || deal.ip_address || "-"}</TableCell>}
                  <TableCell className="text-[#ebebeb]/60 text-sm font-mono">{formatDate(deal.created_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button className="p-2 text-[#ebebeb]/60 hover:text-white transition-colors" onClick={() => openDealDetails(deal)} data-testid={`view-btn-${deal.id}`}><Eye className="w-4 h-4" /></button>
                      {canEdit && <button className="p-2 text-red-400 hover:text-red-300 transition-colors" onClick={() => handleDelete(deal.id)} data-testid={`delete-btn-${deal.id}`}><Trash2 className="w-4 h-4" /></button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Deal Details Modal */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="sm:max-w-[700px] bg-[#191b1d] border-white/10 text-white max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-mono text-xl">Deal Details</DialogTitle>
            <DialogDescription className="text-[#ebebeb]/50">{selectedDeal?.company} - {selectedDeal?.dsp}</DialogDescription>
          </DialogHeader>
          {selectedDeal && (
            <div className="space-y-6 mt-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                {[
                  ["Name", selectedDeal.name], ["Email", selectedDeal.email],
                  ["Company", selectedDeal.company], ["DSP", selectedDeal.dsp],
                  ["Seat #", selectedDeal.dsp_seat_number], ["Status", selectedDeal.status],
                  ["Media Types", selectedDeal.media_type?.join(", ")], ["Devices", selectedDeal.device?.join(", ")],
                  ["Environments", selectedDeal.environment?.join(", ")], ["Geography", selectedDeal.geography?.join(", ")],
                ].map(([label, value]) => (
                  <div key={label}><p className="text-[#ebebeb]/40 font-mono text-xs mb-1">{label}</p><p className="text-white font-mono">{value || "-"}</p></div>
                ))}
              </div>
              {selectedDeal.primary_kpi && (
                <div><p className="text-[#ebebeb]/40 font-mono text-xs mb-1">Primary KPI</p><p className="text-white font-mono">{selectedDeal.primary_kpi}: {selectedDeal.primary_kpi_value} {selectedDeal.primary_kpi_currency}</p></div>
              )}
              {selectedDeal.custom_request && (
                <div><p className="text-[#ebebeb]/40 font-mono text-xs mb-1">Custom Request</p><p className="text-white font-mono text-sm bg-white/5 p-3 rounded-lg">{selectedDeal.custom_request}</p></div>
              )}
              {canEdit && (
                <div className="border-t border-white/10 pt-4">
                  <h3 className="text-sm font-bold font-mono mb-3">Assigned User</h3>
                  <p className="text-[#ebebeb]/40 text-xs font-mono mb-2">
                    Controls whose marketplace reporting includes this deal's data.
                  </p>
                  <div className="flex items-center gap-2">
                    <select value={ownerDraft} onChange={(e) => setOwnerDraft(e.target.value)} className="form-input flex-1 text-sm" data-testid="deal-owner-select">
                      <option value="">Unassigned</option>
                      {users.map((u) => (<option key={u.id} value={u.id}>{userLabel(u)}</option>))}
                    </select>
                    <button onClick={handleAssignOwner} disabled={assigning || ownerDraft === (selectedDeal.user_id || "")} className="ingenium-button" data-testid="deal-owner-save">
                      {assigning ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}
                    </button>
                  </div>
                </div>
              )}
              {canEdit && (
                <div className="border-t border-white/10 pt-4">
                  <h3 className="text-sm font-bold font-mono mb-3">SSP Deal IDs</h3>
                  {sspDeals.map((entry, i) => (
                    <div key={i} className="flex items-center gap-2 mb-2 bg-white/5 p-2 rounded-lg text-sm">
                      <span className="font-mono text-[#ff3737] font-medium">{entry.ssp}</span>
                      <span className="text-[#ebebeb]/40">|</span>
                      <span className="font-mono text-[#ebebeb]/70 flex-1 truncate" title={entry.deal_name}>{entry.deal_name || "Auto-generated"}</span>
                      <span className="text-[#ebebeb]/40">|</span>
                      <span className="font-mono text-white">{entry.deal_id}</span>
                      <button onClick={() => handleRemoveSsp(i)} className="ml-auto text-red-400 hover:text-red-300"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  ))}
                  <div className="flex items-end gap-2 mt-3">
                    <div className="flex-1">
                      <label className="text-[#ebebeb]/40 text-xs font-mono">SSP</label>
                      <select value={newSsp} onChange={(e) => setNewSsp(e.target.value)} className="form-input w-full text-sm mt-1">
                        <option value="">Select SSP</option>
                        {SSP_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="flex-1">
                      <label className="text-[#ebebeb]/40 text-xs font-mono">Deal ID</label>
                      <Input value={newSspDealId} onChange={(e) => setNewSspDealId(e.target.value)} placeholder="Deal ID" className="form-input text-sm mt-1" />
                    </div>
                    <div className="flex-1">
                      <label className="text-[#ebebeb]/40 text-xs font-mono">Deal Name (optional)</label>
                      <Input value={newSspDealName} onChange={(e) => setNewSspDealName(e.target.value)} placeholder="Auto-generated" className="form-input text-sm mt-1" />
                    </div>
                    <button onClick={handleAddSsp} className="p-2 bg-[#ff3737] text-white rounded-lg"><Plus className="w-4 h-4" /></button>
                  </div>
                  <button onClick={handleSaveSspDeals} disabled={savingSsp} className="ingenium-button w-full mt-4" data-testid="save-ssp-btn">
                    {savingSsp ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Save SSP Deals"}
                  </button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AddDealModal
        open={addOpen}
        onOpenChange={setAddOpen}
        token={token}
        users={users}
        onCreated={(deal) => setDeals(prev => [deal, ...prev])}
      />
    </>
  );
};

export default DealsTab;
